import { useEffect, useRef } from 'react';

// Click and hover for one layer. Hands back raw `queryRenderedFeatures` results and lets the
// caller decide what they mean.
//
// `enabled` exists for the lasso: mapbox's own `click` still fires while the lasso is armed, so
// with both live a single click toggles twice and cancels itself out.
function FeatureClick({ map, layer, enabled = true, onClick, onHover }) {
    // Handlers change identity every parent render; a ref keeps the effect off them. Assigned in
    // an effect, not during render, so an abandoned render can't leave a stale handler behind.
    const handlers = useRef({ onClick, onHover });

    useEffect(() => {
        handlers.current = { onClick, onHover };
    });

    useEffect(() => {
        if (!map || !enabled) return undefined;

        const canvas = map.getCanvas();

        // queryRenderedFeatures throws on a layer not yet added, and Layer adds async on mount.
        const query = (point) => (map.getLayer(layer) ? map.queryRenderedFeatures(point, { layers: [layer] }) : []);

        const handleClick = (e) => handlers.current.onClick?.(query(e.point));

        const handleMove = (e) => {
            const features = query(e.point);
            canvas.style.cursor = features.length ? 'pointer' : '';
            handlers.current.onHover?.(features);
        };

        const handleLeave = () => {
            canvas.style.cursor = '';
            handlers.current.onHover?.([]);
        };

        map.on('click', handleClick);
        map.on('mousemove', handleMove);
        map.on('mouseout', handleLeave);

        return () => {
            map.off('click', handleClick);
            map.off('mousemove', handleMove);
            map.off('mouseout', handleLeave);
            // Safe: the lasso's crosshair lives on the canvas *container*, not the canvas.
            canvas.style.cursor = '';
            handlers.current.onHover?.([]);
        };
    }, [map, layer, enabled]);

    return null;
}

export default FeatureClick;
